import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import apiService from '../services/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (ruc: string, password: string, recaptcha_token?: string) => Promise<boolean>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const storedUser = apiService.getCurrentUser();
      
      if (storedUser) {
        // Esperar a que el fingerprint esté listo antes de verificar la sesión
        try {
          await apiService.waitForFingerprint();
          const response = await apiService.refreshToken();
          if (response.success) {
            setUser(response.data.user);
          } else {
            // Sesión inválida, limpiar
            apiService.logout();
            setUser(null);
          }
        } catch (error) {
          // Error al verificar sesión, limpiar
          console.warn('Sesión expirada o inválida, requiere login');
          apiService.logout();
          setUser(null);
        }
      }
    } catch (error) {
      console.error('Error inicializando autenticación:', error);
      apiService.logout();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (ruc: string, password: string, recaptcha_token?: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      // Asegurar que el fingerprint esté listo antes del login
      await apiService.waitForFingerprint();
      const response = await apiService.login(ruc, password, recaptcha_token);
      
      if (response.success) {
        setUser(response.data.user);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error en login:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    apiService.logout();
    setUser(null);
  };

  const refreshAuth = async () => {
    try {
      const response = await apiService.refreshToken();
      if (response.success) {
        setUser(response.data.user);
      }
    } catch (error) {
      console.error('Error refrescando auth:', error);
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export default AuthContext;
